import {useAuth} from '@apps-common/auth-react';
import {Divider} from '@mui/material';
import {Map, TileLayer, VectorLayer, VectorTileLayer} from '@planet/layers';
import {buffer, getWidth} from 'ol/extent';
import {fromExtent} from 'ol/geom/Polygon';
import {transform} from 'ol/proj';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {usePopup} from '../hooks/popup.jsx';
import {getImageryURL} from '../util/collection.js';
import {getFeatureFromEvent, getMosaicsByDate} from '../util/map.js';
import {getDrawStyle} from '../util/styles.js';

const SideBySide = ({
  compare,
  detectionsURL,
  featureStyle,
  imageryURL,
  onSelectResult,
  resultInfo,
  view,
  setView,
  mosaicsInfo,
}) => {
  const [hovering, updateHovering] = useState(false);
  const [coordinate, setCoordinate] = useState(null);
  const [left, setLeft] = useState(null);
  const [leftClick, setLeftClick] = useState(null);
  const [extent, setExtent] = useState(null);
  const auth = useAuth();

  const leftMapRef = useRef();
  const rightMapRef = useRef();

  const selectedStyle = useMemo(() => getDrawStyle('#001F23'), []);

  const onPointerMove = useCallback(
    event => {
      const feature = getFeatureFromEvent(event);
      updateHovering(!!feature);
      if (!event.dragging) {
        setLeft(event.target === leftMapRef.current);
        setCoordinate(transform(event.coordinate, 'EPSG:3857', 'EPSG:4326'));
      }
    },
    [updateHovering],
  );

  const popupCallback = useCallback((event, feature, extent) => {
    if (feature) {
      setLeftClick(event.target === leftMapRef.current);
      setExtent(buffer(extent, getWidth(extent) * 0.15));
    } else {
      setExtent(null);
    }
  }, []);

  const [onMapClick] = usePopup(
    popupCallback,
    true,
    onSelectResult,
    resultInfo,
  );

  useEffect(() => {
    leftMapRef.current?.on('pointermove', onPointerMove);
    leftMapRef.current?.on('click', onMapClick);
    rightMapRef.current?.on('pointermove', onPointerMove);
    rightMapRef.current?.on('click', onMapClick);
    return () => {
      leftMapRef.current?.un('pointermove', onPointerMove);
      leftMapRef.current?.un('click', onMapClick);
      rightMapRef.current?.un('pointermove', onPointerMove);
      rightMapRef.current?.un('click', onMapClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftMapRef.current, onMapClick, onPointerMove, rightMapRef.current]);

  return (
    <>
      <Map
        style={{
          cursor: hovering ? 'pointer' : 'default',
          width: '50%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        innerRef={node => (leftMapRef.current = node)}
        animation={{duration: 0}}
        view={view}
        onViewChange={setView}
      >
        {!!compare && (
          <TileLayer
            url={getImageryURL(
              `Mosaic:${
                getMosaicsByDate(mosaicsInfo, new Date(compare.observed))[0]
                  .name
              }`,
              auth.user?.accessToken,
            )}
          />
        )}
        {!!compare && detectionsURL && (
          <VectorTileLayer layerStyle={featureStyle} url={detectionsURL} />
        )}
        {!!compare && !left && coordinate && (
          <VectorLayer
            data={{
              type: 'Feature',
              geometry: {type: 'Point', coordinates: coordinate},
            }}
          />
        )}
        {!!leftClick && !!extent && (
          <VectorLayer
            layerStyle={selectedStyle}
            data={{
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: fromExtent(extent).getCoordinates(),
              },
            }}
          />
        )}
      </Map>
      <Divider orientation="vertical" sx={{backgroundColor: '#fff'}} />
      <Map
        style={{
          cursor: hovering ? 'pointer' : 'default',
          width: '50%',
          height: '100%',
          position: 'absolute',
          top: 0,
          right: 0,
        }}
        innerRef={node => (rightMapRef.current = node)}
        animation={{duration: 0}}
        view={view}
        onViewChange={setView}
      >
        <TileLayer url={imageryURL} />
        {detectionsURL && (
          <VectorTileLayer layerStyle={featureStyle} url={detectionsURL} />
        )}
        {!!left && coordinate && (
          <VectorLayer
            data={{
              type: 'Feature',
              geometry: {type: 'Point', coordinates: coordinate},
            }}
          />
        )}
        {!leftClick && !!extent && (
          <VectorLayer
            layerStyle={selectedStyle}
            data={{
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: fromExtent(extent).getCoordinates(),
              },
            }}
          />
        )}
      </Map>
    </>
  );
};

SideBySide.propTypes = {
  compare: PropTypes.object,
  detectionsURL: PropTypes.string,
  featureStyle: PropTypes.func,
  imageryURL: PropTypes.string,
  onSelectResult: PropTypes.func,
  resultInfo: PropTypes.object,
  view: PropTypes.object,
  setView: PropTypes.func,
  mosaicsInfo: PropTypes.array,
};

export default SideBySide;
